import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Loyaltic } from './api-url.constants';
import { OpenMeteoArchiveResponse, WeatherLocationResolution, WeatherLookupResult, WeatherOcrContext } from '../models/weather-bonus.models';

@Injectable({
  providedIn: 'root'
})
export class WeatherService {
  private readonly archiveUrl = environment.baseUrl + Loyaltic.API.WeatherHistory;
  private readonly observationWindowMinutes = 60;
  private readonly rainyWeatherCodes = [51, 53, 55, 61, 63, 65, 66, 67, 80, 81, 82];
  public lastDebugResponse: OpenMeteoArchiveResponse | null = null;

  constructor(private http: HttpClient) {}

  async fetchHourlyWeather(context: WeatherOcrContext, location: WeatherLocationResolution): Promise<WeatherLookupResult | null> {
    if (location.latitude == null || location.longitude == null || !context.purchaseLocalDateTime) {
      return null;
    }

    const purchaseDate = context.purchaseLocalDateTime.split('T')[0];
    const params = new HttpParams()
      .set('latitude', String(location.latitude))
      .set('longitude', String(location.longitude))
      .set('startDate', purchaseDate)
      .set('endDate', purchaseDate)
      .set('timezone', context.timezone || 'Europe/Stockholm');

    const response = await this.http.get<OpenMeteoArchiveResponse>(this.archiveUrl, { params }).toPromise();
    this.lastDebugResponse = response || null;
    if (!response || !response.hourly || !response.hourly.time || response.hourly.time.length === 0) {
      return null;
    }

    const purchaseTime = this.parseLocalDateTime(context.purchaseLocalDateTime);
    if (!purchaseTime) {
      return null;
    }

    const windowMillis = this.observationWindowMinutes * 60 * 1000;
    const purchaseLocalDate = this.getLocalDatePart(context.purchaseLocalDateTime);
    const hourly = response.hourly as any;
    const weatherCodes = this.getHourlySeries(hourly, 'weather_code', 'weatherCode');
    const precipitationValues = this.getHourlySeries(hourly, 'precipitation');
    const temperatureValues = this.getHourlySeries(hourly, 'temperature_2m', 'temperature2m');
    const candidates: Array<{ index: number; distance: number; isRaining: boolean; precipitation: number | null }> = [];

    response.hourly.time.forEach((hour, index) => {
      if (this.getLocalDatePart(hour) !== purchaseLocalDate) {
        return;
      }

      const observationTime = this.parseLocalDateTime(hour);
      if (!observationTime) {
        return;
      }

      const age = purchaseTime.getTime() - observationTime.getTime();
      if (age < 0 || age > windowMillis) {
        return;
      }

      const weatherCode = Number((weatherCodes || [])[index]);
      const precipitation = this.getHourlyValue(precipitationValues, index);
      const isRaining = (precipitation || 0) > 0 || this.rainyWeatherCodes.indexOf(weatherCode) !== -1;
      candidates.push({ index, distance: age, isRaining, precipitation });
    });

    if (candidates.length === 0) {
      return null;
    }

    candidates.sort((left, right) => {
      if (left.isRaining !== right.isRaining) {
        return left.isRaining ? -1 : 1;
      }

      if ((left.precipitation || 0) !== (right.precipitation || 0)) {
        return (right.precipitation || 0) - (left.precipitation || 0);
      }

      return left.distance - right.distance;
    });

    const bestIndex = candidates[0].index;
    const observationTimeLocal = response.hourly.time[bestIndex];
    const provider = this.detectProvider(response);
    const weatherCode = this.getHourlyValue(weatherCodes, bestIndex);

    return {
      provider,
      providerRequestTimeUtc: new Date().toISOString(),
      observationTimeUtc: this.toUtcIsoString(observationTimeLocal, context.timezone || 'Europe/Stockholm'),
      observationTimeLocal,
      weatherCode: weatherCode === null ? '' : String(weatherCode),
      precipitationMm: this.getHourlyValue(precipitationValues, bestIndex),
      temperatureC: this.getHourlyValue(temperatureValues, bestIndex),
      observationWindowMinutes: this.observationWindowMinutes,
      matchedObservationCount: candidates.length
    };
  }

  private parseLocalDateTime(value: string): Date | null {
    if (!value) {
      return null;
    }

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!match) {
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      Number(match[6] || 0)
    );
  }

  private getLocalDatePart(value: string): string {
    return (value || '').split('T')[0];
  }

  private getHourlyValue(values: number[] | undefined, index: number): number | null {
    if (!values || values.length <= index || values[index] == null) {
      return null;
    }
    return values[index];
  }

  private getHourlySeries(hourly: any, primaryName: string, fallbackName?: string): number[] | undefined {
    if (!hourly) {
      return undefined;
    }

    return hourly[primaryName] || (fallbackName ? hourly[fallbackName] : undefined);
  }

  private toUtcIsoString(localDateTime: string, timezone: string): string {
    const localDate = this.parseLocalDateTime(localDateTime);
    if (!localDate) {
      return '';
    }

    const offsetMinutes = this.getTimezoneOffsetMinutes(localDate, timezone);
    return new Date(localDate.getTime() - offsetMinutes * 60 * 1000).toISOString();
  }

  private getTimezoneOffsetMinutes(date: Date, timezone: string): number {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    const parts: any = {};
    formatter.formatToParts(date).forEach(part => {
      if (part.type !== 'literal') {
        parts[part.type] = part.value;
      }
    });

    const timezoneDate = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    const localAsUtc = Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    );

    return (timezoneDate - localAsUtc) / 60000;
  }

  private detectProvider(response: any): string {
    if (!response) {
      return 'unknown';
    }

    if (response.provider) {
      return String(response.provider);
    }

    if (response.resolvedAddress || response.address) {
      return 'visual-crossing';
    }

    return 'open-meteo';
  }
}
